import { Button, Tooltip, Flex, Typography } from 'antd';
import { LuDownload, LuFilter, LuRotateCcw } from 'react-icons/lu';
import { useCollectionStore } from '@stores/useCollectionStore';
import { ACTIVE_FILTER_STYLE } from '../constants';

const { Text } = Typography;

/**
 * Header for the Detection Results (Collection) section.
 *
 * @param {{
 *   onFilterClick: function,
 *   disabled: boolean,
 *   onResetPointers?: function,
 *   canResetPointers?: boolean,
 *   onDownload?: function,
 *   isDownloading?: boolean,
 *   canDownload?: boolean,
 * }} props
 */
const CollectionHeader = ({
	onFilterClick,
	disabled,
	onResetPointers,
	canResetPointers,
	onDownload,
	isDownloading,
	canDownload,
}) => {
	const activeFilterCount = useCollectionStore(s => s.activeFilterCount);

	const filterTitle = activeFilterCount > 0 ? `Filter (${activeFilterCount} active)` : 'Filter';

	// The export endpoint reads an empty filter map as "export everything", which
	// is the opposite of "nothing is shown" — so with no object visible there is
	// no correct file to write, and the control is closed rather than guessed at.
	const downloadTitle = canDownload
		? 'Download detections (GeoJSON)'
		: 'Show at least one object to download';

	return (
		<Flex vertical>
			<Flex align="center" gap={8}>
				<Text
					type="secondary"
					style={{
						fontWeight: 400,
						fontSize: '0.75rem',
						textTransform: 'uppercase',
						letterSpacing: '0.05em',
					}}>
					Detection Results
				</Text>

				{/* Hard right, so it shares one action column with the rows below. */}
				<Flex align="center" gap={12} style={{ marginLeft: 'auto' }}>
					{/* Labeled: beside a filter icon a bare ↺ reads as "reset filters". */}
					{canResetPointers && onResetPointers && (
						<Tooltip title="Return every object to its default shape">
							<Button
								type="text"
								size="small"
								icon={<LuRotateCcw />}
								onClick={onResetPointers}
								style={{ fontSize: '0.75rem' }}>
								Reset shapes
							</Button>
						</Tooltip>
					)}

					{onDownload && (
						// The span carries the tooltip: a disabled button fires no
						// mouse events, and the disabled case is the one that most
						// needs explaining.
						<Tooltip title={downloadTitle}>
							<span style={{ display: 'inline-flex' }}>
								<Button
									type="text"
									size="small"
									icon={<LuDownload />}
									loading={isDownloading}
									disabled={disabled || !canDownload}
									onClick={onDownload}
									aria-label={downloadTitle}
								/>
							</span>
						</Tooltip>
					)}

					<Tooltip title={filterTitle}>
						<Button
							type="text"
							size="small"
							icon={<LuFilter />}
							disabled={disabled}
							onClick={onFilterClick}
							aria-label={filterTitle}
							style={
								activeFilterCount > 0 && !disabled ? ACTIVE_FILTER_STYLE : undefined
							}
						/>
					</Tooltip>
				</Flex>
			</Flex>
		</Flex>
	);
};

export default CollectionHeader;
